// See NotificationCenterPage.spec.md for NCP-FR-002, NCP-FR-004 and NCP-FR-010

import type { BadgeVariant } from 'core/elements/badge/Badge'
import { escapeRegex } from 'utils/misc'
import type { AuditEvent } from './models'

export const EMPTY_VALUE = '-'
export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000'

// Friendly labels for resource types whose name reads poorly after generic formatting
// (older audit records call VMs "servers", newer ones "vm")
const RESOURCE_TYPE_LABELS: Record<string, string> = {
  'service/compute/server': 'Virtual Machine',
  servers: 'Virtual Machine',
  server: 'Virtual Machine',
  vm: 'Virtual Machine',
  floatingips: 'Floating IP',
  // Volume types (the volume service reports them as plain "type" / "types")
  type: 'Volume Type',
  types: 'Volume Type',
}

// The audit service reports missing names/IDs as the literal string "unknown"
const isKnown = (value?: string): boolean => !!value && value.toLowerCase() !== 'unknown'

const sentenceCase = (text: string) => {
  const lower = text.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

const titleCase = (text: string) =>
  text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// "security groups" → "security group", "policies" → "policy"; leaves "status", "address" alone
const singularize = (words: string) =>
  words.replace(/(\w+)$/, (word) => {
    if (/ies$/i.test(word)) return `${word.slice(0, -3)}y`
    if (/(ss|us)$/i.test(word) || !/s$/i.test(word)) return word
    return word.slice(0, -1)
  })

// Words from one identifier segment, without the OpenStack "os-" extension prefix
const toWords = (segment: string) =>
  segment
    .replace(/^os-/i, '')
    .replace(/[._-]+/g, ' ')
    .trim()

// NCP-FR-004: "created.user" → "Created user"; server/volume actions such as
// "update/os-start" or "update/os-set_bootable" show the action itself ("Start", "Set bootable")
export const formatActionName = (actionName?: string): string => {
  const action = (actionName || '').split('/').filter(Boolean).pop() || ''
  const words = toWords(action)
  return words ? sentenceCase(words) : EMPTY_VALUE
}

// NCP-FR-004: "data/security/account/user" → "User", "security-groups" → "Security Group";
// unmapped types use the last path segment, singularized
export const formatResourceType = (resourceType?: string): string => {
  if (!resourceType) return EMPTY_VALUE
  if (RESOURCE_TYPE_LABELS[resourceType]) return RESOURCE_TYPE_LABELS[resourceType]
  const lastSegment = resourceType.split('/').filter(Boolean).pop() || ''
  const words = toWords(lastSegment)
  return words ? titleCase(singularize(words)) : EMPTY_VALUE
}

// NCP-FR-004: The Event column shows the event with its object type ("Authenticate : User",
// "Stop : Virtual Machine"). The type is left out when it is unknown or when the event already
// names it ("Created role assignment" + "Role Assignment"), so it isn't repeated.
export const formatEventWithObjectType = (event: string, objectType: string): string => {
  if (objectType === EMPTY_VALUE) return event
  if (event === EMPTY_VALUE) return objectType
  const namesType = new RegExp(`\\b${escapeRegex(objectType)}\\b`, 'i').test(event)
  return namesType ? event : `${event} : ${objectType}`
}

// NCP-FR-004: Username, else user ID; the all-zero user ID is the system itself
export const formatInitiator = ({ username, user_id }: Partial<AuditEvent>): string => {
  if (isKnown(username)) return username
  if (user_id === SYSTEM_USER_ID) return 'System'
  return isKnown(user_id) ? user_id : EMPTY_VALUE
}

// NCP-FR-004: Resource name, else resource ID ("unknown" counts as missing)
export const formatObject = ({ resource_name, resource_id }: Partial<AuditEvent>): string => {
  if (isKnown(resource_name)) return resource_name
  return isKnown(resource_id) ? resource_id : EMPTY_VALUE
}

// NCP-FR-004: Badge colour for the Result column
export const getResultVariant = (result?: string): BadgeVariant => {
  switch ((result || '').toLowerCase()) {
    case 'success':
      return 'success'
    case 'failure':
    case 'fail':
      return 'error'
    default:
      return 'unknown'
  }
}

// NCP-FR-002: Parse an NDJSON body, skipping blank, malformed and non-object lines
export const parseNdjson = <T extends object>(text?: string): T[] =>
  (text || '').split(/\r?\n/).reduce<T[]>((records, line) => {
    const trimmed = line.trim()
    if (!trimmed) return records
    try {
      const record = JSON.parse(trimmed)
      if (record && typeof record === 'object' && !Array.isArray(record)) records.push(record)
    } catch {
      // Malformed line: skip it so one bad record doesn't hide the rest
    }
    return records
  }, [])

// NCP-FR-004, NB-FR-003: Audit events carry no ID; this one is stable across loads and polls
export const getAuditEventId = (event: AuditEvent): string =>
  [event.timestamp_utc, event.request_id, event.action_name, event.resource_id].join('|')

// Milliseconds since the epoch, or null for a missing or unparseable timestamp (shared with the
// notification bar's unread helpers)
export const parseTimestamp = (timestamp?: string | null): number | null => {
  const time = Date.parse(timestamp ?? '')
  return Number.isNaN(time) ? null : time
}

type Timestamped = Pick<AuditEvent, 'timestamp_utc'>

// Sort key: events with a missing or invalid timestamp count as the oldest
const sortTime = ({ timestamp_utc }: Timestamped): number =>
  parseTimestamp(timestamp_utc) ?? -Infinity

// NCP-FR-002: Newest first; events with a missing or invalid timestamp sort last. Each timestamp
// is parsed once (not on every comparison), and the sort is stable.
export const sortNewestFirst = <T extends Timestamped>(events: T[]): T[] =>
  events
    .map((event) => ({ event, time: sortTime(event) }))
    .sort((a, b) => b.time - a.time || 0)
    .map(({ event }) => event)

// Two lists that are each newest first, merged into one (O(n + m)); ties keep `a` first
const mergeNewestFirst = <T extends Timestamped>(a: T[], b: T[]): T[] => {
  const merged: T[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    merged.push(sortTime(a[i]) >= sortTime(b[j]) ? a[i++] : b[j++])
  }
  return merged.concat(a.slice(i), b.slice(j))
}

// NCP-FR-009: The newest timestamp among events listed newest first (null if none is usable); a
// background refresh only asks for events from this time on
export const newestLoadedTimestamp = (events: AuditEvent[]): string | null =>
  events.find(({ timestamp_utc }) => parseTimestamp(timestamp_utc) !== null)?.timestamp_utc ?? null

// NCP-FR-009: A background refresh's events merged into the loaded ones, newest first. Events
// already loaded (the refresh starts at the newest loaded time, so it repeats those) are not
// added twice, and loaded events that have left the window are dropped. `loaded` is already
// newest first, so only the new events are sorted, then the two lists are merged.
export const mergeNewEvents = (
  fresh: AuditEvent[],
  loaded: AuditEvent[],
  windowStart: string,
): AuditEvent[] => {
  const loadedIds = new Set(loaded.map(getAuditEventId))
  const start = parseTimestamp(windowStart)
  const added = sortNewestFirst(fresh.filter((event) => !loadedIds.has(getAuditEventId(event))))
  return mergeNewestFirst(added, loaded).filter(({ timestamp_utc }) => {
    const time = parseTimestamp(timestamp_utc)
    return start === null || time === null || time >= start
  })
}

// NCP-FR-010: Resource types whose details page the Object cell links to. Both record formats are
// covered: older ("servers", "volumes", "snapshots"; no resource name) and newer ("vm",
// "volume", "snapshot"; named).
export type LinkableResource = 'vm' | 'volume' | 'snapshot'
const LINKABLE_RESOURCE_TYPES: Record<string, LinkableResource> = {
  'service/compute/server': 'vm',
  servers: 'vm',
  server: 'vm',
  vm: 'vm',
  volumes: 'volume',
  volume: 'volume',
  snapshots: 'snapshot',
  snapshot: 'snapshot',
}

// The snapshot details page only finds snapshots in the app's current tenant; VM and volume
// details fetch by ID across tenants for admins
const TENANT_SCOPED_RESOURCES: ReadonlySet<LinkableResource> = new Set(['snapshot'])

// Audit records write project IDs as dashed UUIDs ("4036efad-ae05-…"); Keystone and the app use
// the same ID without dashes, so IDs are compared without dashes and case
const sameId = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && a.replace(/-/g, '').toLowerCase() === b.replace(/-/g, '').toLowerCase()

export const getLinkableResource = (resourceType?: string): LinkableResource | undefined =>
  resourceType ? LINKABLE_RESOURCE_TYPES[resourceType] : undefined

// NCP-FR-010: What one event's row can learn from the other loaded events
export interface EventRowContext {
  // Resource ID → name, from the events that carry a name (older records report "unknown")
  names: Map<string, string>
  // Resources whose newest loaded event deletes them; they have no page to open
  deletedIds: Set<string>
  // The region the app is in; another region's resources can't be opened from here
  activeRegion?: string | null
  // The tenant the app is in; tenant-scoped pages (snapshots) only open its resources
  activeTenantId?: string | null
}

// NCP-FR-010: A successful delete of the resource itself. The audit service also logs a volume
// detach as a "delete" on the volume, with the removed attachment in its metadata; that delete is
// about the attachment, and the volume carries on.
// Deletes: "delete", and force deletes sent as actions ("update/os-force_delete" for volumes and
// snapshots, "update/forceDelete" for VMs)
const DELETE_ACTION = /^(delete|.*\/(os-)?force_?delete)$/i

const deletesResource = ({ action_name, action_result, resource_id, metadata }: AuditEvent) =>
  DELETE_ACTION.test(action_name ?? '') &&
  action_result?.toLowerCase() === 'success' &&
  !metadata?.some(({ id }) => isKnown(id) && id !== resource_id)

export const getEventRowContext = (
  events: AuditEvent[],
  activeRegion?: string | null,
  activeTenantId?: string | null,
): EventRowContext => {
  const names = new Map<string, string>()
  const deletedIds = new Set<string>()
  const seenIds = new Set<string>()
  // Events are newest first, so the first event seen for a resource is its latest one
  for (const event of events) {
    const { resource_id, resource_name } = event
    if (!isKnown(resource_id)) continue
    if (isKnown(resource_name) && !names.has(resource_id)) names.set(resource_id, resource_name)
    // Only the latest event decides: a resource used after a delete still exists
    if (!seenIds.has(resource_id) && deletesResource(event)) deletedIds.add(resource_id)
    seenIds.add(resource_id)
  }
  return { names, deletedIds, activeRegion, activeTenantId }
}

// NCP-FR-010: The event's own resource name, else one another event gave the same resource
export const resolveResourceName = (
  { resource_name, resource_id }: Partial<AuditEvent>,
  context?: EventRowContext,
): string | undefined =>
  isKnown(resource_name) ? resource_name : (context?.names.get(resource_id) ?? resource_name)

// NCP-FR-010: Which details page the row's Object opens, if any: a VM, volume or snapshot with a
// known ID, not deleted, in the app's current region (events without a region are assumed
// local); a snapshot also only in the app's current tenant
export const getObjectLinkType = (
  event: Partial<AuditEvent>,
  context?: EventRowContext,
): LinkableResource | undefined => {
  const resource = getLinkableResource(event.resource_type)
  if (!resource || !context || !isKnown(event.resource_id)) return undefined
  if (context.deletedIds.has(event.resource_id)) return undefined
  const { activeRegion } = context
  if (activeRegion && isKnown(event.region) && event.region !== activeRegion) return undefined
  const { activeTenantId } = context
  if (TENANT_SCOPED_RESOURCES.has(resource) && !sameId(event.project_id, activeTenantId)) {
    return undefined
  }
  return resource
}

// NCP-FR-007: The Action and Result filters, applied to the returned events too. The export API
// doesn't apply the result filter (a "failure" request returns successes), so the page can't rely
// on it; when the API does filter, this changes nothing.
export const matchesEventFilters = (
  { action_name, action_result }: Partial<AuditEvent>,
  { action, result }: { action?: string; result?: string },
): boolean =>
  (!action || action_name === action) &&
  (!result || action_result?.toLowerCase() === result.toLowerCase())
